// =============================
// File: src/@utility/app/analytics.js
// Notes (TH):
// - ฟังก์ชันกลางสำหรับ Tracking ผ่าน GA4 และ Amplitude พร้อมกัน
// - GA4: ใช้ gtag() โดยตรง (ไม่ผ่าน GTM) เพื่อรองรับ Event ที่ต้องการข้อมูลจาก API Response
// - Amplitude: ใช้ @amplitude/analytics-browser SDK
// - ถ้าไม่มี Key ของตัวใด ให้ข้ามทันที และจะไม่ทำให้ระบบล่ม
// - ห้าม throw Error ออกมาจากฟังก์ชันนี้เด็ดขาด
// =============================

import * as amplitude from '@amplitude/analytics-browser'

// ─── Keys จาก .env ───────────────────────────────────────────────────────────
const GA4_ID = process.env.REACT_APP_GOOGLE_KEY || ''
const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_KEY || ''
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || ''

// ─── Flags ───────────────────────────────────────────────────────────────────
const isBrowser = typeof window !== 'undefined'
const isDev = process.env.NODE_ENV !== 'production'

const isGA4Enabled = Boolean(GA4_ID)
const isAmplitudeEnabled = Boolean(AMPLITUDE_API_KEY)
const isClarityEnabled = Boolean(CLARITY_ID)

let ga4Initialized = false
let amplitudeInitialized = false
let clarityInitialized = false

// ─── Dev Logger ──────────────────────────────────────────────────────────────
const logDev = (...args) => {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[analytics]', ...args)
    }
}

// ─── โหลด GA4 Script เข้า <head> ─────────────────────────────────────────────
const loadGA4Script = () => {
    if (!isBrowser || !isGA4Enabled) return

    const existedScript = document.querySelector(
        `script[src="https://www.googletagmanager.com/gtag/js?id=${GA4_ID}"]`
    )

    if (!existedScript) {
        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
        document.head.appendChild(script)
    }

    window.dataLayer = window.dataLayer || []
    window.gtag =
        window.gtag ||
        function gtag() {
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments)
        }
}

// ─── โหลด Microsoft Clarity Script เข้า <head> ─────────────────────────────────
const loadClarityScript = () => {
    if (!isBrowser || !isClarityEnabled) return

    const existedScript = document.querySelector(
        `script[src^="https://www.clarity.ms/tag/${CLARITY_ID}"]`
    )

    if (!existedScript) {
        ;(function (c, l, a, r, i, t, y) {
            c[a] =
                c[a] ||
                function () {
                    ;(c[a].q = c[a].q || []).push(arguments)
                }
            t = l.createElement(r)
            t.async = 1
            t.src = 'https://www.clarity.ms/tag/' + i
            y = l.getElementsByTagName(r)[0]
            if (y && y.parentNode) {
                y.parentNode.insertBefore(t, y)
            } else {
                l.head.appendChild(t)
            }
        })(window, document, 'clarity', 'script', CLARITY_ID)
    }
}

// ─── Sanitize Params (กัน undefined / function / nested object ───────────────
const sanitizeParams = (params = {}) => {
    if (!params || typeof params !== 'object') return {}

    const result = {}
    Object.keys(params).forEach((key) => {
        const value = params[key]
        if (typeof value === 'undefined' || typeof value === 'function') return
        if (
            value === null ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
        ) {
            result[key] = value
            return
        }
        if (Array.isArray(value) || typeof value === 'object') {
            result[key] = JSON.stringify(value)
        }
    })
    return result
}

// ─── Base Params ที่ติดไปทุก Event ──────────────────────────────────────────
const getBaseParams = () => {
    const baseParams = {
        platform: 'web',
        app_env: process.env.NODE_ENV || 'development',
        timestamp: Date.now()
    }
    if (isBrowser) {
        baseParams.page_path = window.location.pathname || ''
        baseParams.page_url = window.location.href || ''
    }
    return baseParams
}

// ─── ส่ง Event ไป GA4 ────────────────────────────────────────────────────────
const sendToGA4 = (eventName, params = {}) => {
    if (!isBrowser || !isGA4Enabled || typeof window.gtag !== 'function') return
    try {
        window.gtag('event', eventName, params)
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('GA4 track error:', error)
    }
}

// ─── ส่ง Event ไป Amplitude ──────────────────────────────────────────────────
const sendToAmplitude = (eventName, params = {}) => {
    if (!isAmplitudeEnabled || !amplitudeInitialized) return
    try {
        amplitude.track(eventName, params)
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Amplitude track error:', error)
    }
}

// ─── ส่ง Event ไป Clarity ────────────────────────────────────────────────────
const sendToClarity = (eventName) => {
    if (!isClarityEnabled || !clarityInitialized || typeof window.clarity !== 'function') return
    try {
        window.clarity('event', eventName)
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Clarity track error:', error)
    }
}

// ─── PUBLIC FUNCTIONS ─────────────────────────────────────────────────────────

/**
 * initAnalytics()
 * เริ่มต้นระบบ GA4 และ Amplitude
 * ควรเรียกครั้งเดียวตอนแอปโหลด (ใน Layouts.js หรือ App.js)
 */
export const initAnalytics = () => {
    try {
        if (isGA4Enabled && !ga4Initialized) {
            loadGA4Script()

            // รอให้ script โหลดเสร็จก่อน config
            const configGA4 = () => {
                if (typeof window.gtag === 'function') {
                    window.gtag('js', new Date())
                    window.gtag('config', GA4_ID, { send_page_view: false })
                    ga4Initialized = true
                    logDev('GA4 initialized with ID:', GA4_ID)
                }
            }

            // ถ้า script โหลดเสร็จแล้ว config ทันที, ไม่งั้นรอ
            if (typeof window.gtag === 'function') {
                configGA4()
            } else {
                const script = document.querySelector(
                    `script[src="https://www.googletagmanager.com/gtag/js?id=${GA4_ID}"]`
                )
                if (script) {
                    script.addEventListener('load', configGA4)
                } else {
                    setTimeout(configGA4, 1000)
                }
            }
        }

        if (isAmplitudeEnabled && !amplitudeInitialized) {
            amplitude.init(AMPLITUDE_API_KEY, {
                defaultTracking: false,
                autocapture: false
            })
            amplitudeInitialized = true
            logDev('Amplitude initialized')
        }

        if (isClarityEnabled && !clarityInitialized) {
            loadClarityScript()
            clarityInitialized = true
            logDev('Clarity initialized with ID:', CLARITY_ID)
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('initAnalytics error:', error)
    }
}

/**
 * setAnalyticsUser(userId, userProperties)
 * ผูก User ID กับ GA4 และ Amplitude หลังจาก Login สำเร็จ
 * @param {string|number} userId
 * @param {object} userProperties - เช่น { username, level, vip }
 */
export const setAnalyticsUser = (userId, userProperties = {}) => {
    if (!userId) return

    const safeUserProperties = sanitizeParams(userProperties)

    try {
        if (isGA4Enabled && typeof window.gtag === 'function') {
            window.gtag('set', { user_id: String(userId) })
        }

        if (isAmplitudeEnabled && amplitudeInitialized) {
            amplitude.setUserId(String(userId))
            if (Object.keys(safeUserProperties).length > 0) {
                const identify = new amplitude.Identify()
                Object.keys(safeUserProperties).forEach((key) => {
                    identify.set(key, safeUserProperties[key])
                })
                amplitude.identify(identify)
            }
        }

        if (isClarityEnabled && typeof window.clarity === 'function') {
            window.clarity('identify', String(userId))
            if (Object.keys(safeUserProperties).length > 0) {
                Object.keys(safeUserProperties).forEach((key) => {
                    window.clarity('set', key, String(safeUserProperties[key]))
                })
            }
        }

        logDev('setAnalyticsUser', { userId: String(userId), userProperties: safeUserProperties })
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('setAnalyticsUser error:', error)
    }
}

/**
 * clearAnalyticsUser()
 * ล้าง User ออกจาก Analytics หลัง Logout
 */
export const clearAnalyticsUser = () => {
    try {
        if (isGA4Enabled && typeof window.gtag === 'function') {
            window.gtag('set', { user_id: undefined })
        }
        if (isAmplitudeEnabled && amplitudeInitialized) {
            amplitude.reset()
        }
        logDev('clearAnalyticsUser')
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('clearAnalyticsUser error:', error)
    }
}

/**
 * trackEvent(eventName, params)
 * ฟังก์ชันกลางสำหรับส่ง Custom Event ไปยัง GA4 และ Amplitude พร้อมกัน
 * @param {string} eventName - ชื่อ Event เช่น "deposit_success"
 * @param {object} params    - ข้อมูลเพิ่มเติม เช่น { entry_page, amount }
 *
 * @example
 * trackEvent("deposit_success", { entry_page: "deposit", amount: 500 })
 */
export const trackEvent = (eventName, params = {}) => {
    if (!eventName) return

    const finalParams = {
        ...getBaseParams(),
        ...sanitizeParams(params)
    }

    try {
        sendToGA4(eventName, finalParams)
        sendToAmplitude(eventName, finalParams)
        sendToClarity(eventName)
        logDev('trackEvent', eventName, finalParams)
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('trackEvent error:', error)
    }
}

/**
 * trackPageView(pageName, params)
 * เก็บ Page View แต่ละหน้า
 * @param {string} pageName - ชื่อหน้า เช่น "home_page", "deposit_page"
 * @param {object} params   - ข้อมูลเพิ่มเติม เช่น { page_group: "home" }
 *
 * @example
 * trackPageView("home_page", { page_group: "home" })
 */
export const trackPageView = (pageName, params = {}) => {
    const finalParams = {
        ...getBaseParams(),
        page_name: pageName || 'unknown_page',
        ...sanitizeParams(params)
    }

    try {
        sendToGA4('page_view', finalParams)
        sendToAmplitude('page_view', finalParams)
        logDev('trackPageView', finalParams)
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('trackPageView error:', error)
    }
}

/**
 * analyticsEnabled
 * ใช้ตรวจสอบว่าเปิดใช้งาน GA4 / Amplitude อยู่หรือไม่
 * @example
 * import { analyticsEnabled } from '@utility/app/analytics'
 * console.log(analyticsEnabled.ga4)       // true/false
 * console.log(analyticsEnabled.amplitude) // true/false
 */
export const analyticsEnabled = {
    ga4: isGA4Enabled,
    amplitude: isAmplitudeEnabled,
    clarity: isClarityEnabled
}
